'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { CLASSNAME, SHIPPING_SLA_TIER } from '@skin-seoul/service';
import type { FormattedShippingSLA } from '@skin-seoul/service';
import { LucideIcon, Skeleton, cn } from '@skin-seoul/design-system';

const TITLE_LEAD = 'Estimated Delivery';
const TITLE_SUFFIX = '(if ordered today)';
const AVERAGE_CONFIDENCE_TEXT = '(70% of orders)';
const LATEST_CONFIDENCE_TEXT = '(90% of orders)';
const LIMITED_DATA_FOOTNOTE = '* Estimates are based on limited data for your selected location.';

const ShippingSLAContext = createContext<FormattedShippingSLA | null>(null);

const useShippingSLAContext = () => {
  const ctx = useContext(ShippingSLAContext);
  if (!ctx) {
    throw new Error('<ShippingSLA.*> subcomponents must be rendered inside <ShippingSLA>');
  }
  return ctx;
};

export interface ShippingSLARootProps {
  /** Formatted SLA payload from `formatShippingSLA` / `useShippingSLA` */
  formatted: FormattedShippingSLA;
  /** Additional wrapper classes */
  className?: string;
  /** Compose using `<ShippingSLA.Header />`, `<ShippingSLA.Dates />`, `<ShippingSLA.Footnote />`, `<ShippingSLA.Pill />` */
  children: ReactNode;
}

/**
 * Compound component for the Dynamic SLA block (SSD-3986 Phase 2).
 * Tier-aware visual states (measured / fallback) and the seasonal pill are
 * driven by `formatted` via context so subcomponents stay declarative.
 *
 * Default composition (PDP / Cart drawer):
 * ```tsx
 * <ShippingSLA formatted={formatted}>
 *   <ShippingSLA.Header />
 *   <ShippingSLA.Dates />
 *   <ShippingSLA.Footnote />
 *   <ShippingSLA.Pill />
 * </ShippingSLA>
 * ```
 */
const ShippingSLARoot = ({ formatted, className, children }: ShippingSLARootProps) => (
  <ShippingSLAContext.Provider value={formatted}>
    <div className={cn('flex flex-col gap-3 items-start', CLASSNAME.NOT_TRANSLATE, className)}>{children}</div>
  </ShippingSLAContext.Provider>
);

export interface ShippingSLAHeaderProps {
  className?: string;
}

const ShippingSLAHeader = ({ className }: ShippingSLAHeaderProps) => (
  <div className={cn('flex gap-2 items-center text-neutral-800 whitespace-nowrap', className)}>
    <span className="text-heading-h6-mobile desktop:text-heading-h6 font-medium">{TITLE_LEAD}</span>
    <span className="text-paragraph-small desktop:text-paragraph-medium font-regular">{TITLE_SUFFIX}</span>
  </div>
);

export interface ShippingSLADatesProps {
  /** Override classes applied to date text spans (e.g. swap to a smaller scale in cards) */
  className?: string;
}

const ShippingSLADates = ({ className }: ShippingSLADatesProps) => {
  const { tier, averageDateText, latestDateText } = useShippingSLAContext();
  const isFallbackTier = tier === SHIPPING_SLA_TIER.Fallback;
  const dateClasses = cn(
    'text-paragraph-medium desktop:text-paragraph-large text-neutral-800 whitespace-nowrap',
    className,
  );

  if (isFallbackTier) {
    return (
      <div className="flex gap-2 items-center">
        <LucideIcon name="CalendarCheck" size={20} className="text-primary-500 shrink-0" />
        <span className={dateClasses}>
          <span className="font-regular">Expected by </span>
          <span className="font-medium">
            {averageDateText} - {latestDateText}
          </span>
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2 items-center">
        <LucideIcon name="CalendarCheck" size={20} className="text-primary-500 shrink-0" />
        <span className={dateClasses}>
          <span className="font-regular">Expected by </span>
          <span className="font-medium">{averageDateText}</span>
        </span>
        <span className="text-label-small font-medium text-neutral-400 whitespace-nowrap">
          {AVERAGE_CONFIDENCE_TEXT}
        </span>
      </div>
      <div className="flex gap-2 items-center">
        <LucideIcon name="Truck" size={20} className="text-primary-500 shrink-0" />
        <span className={dateClasses}>
          <span className="font-regular">Latest by </span>
          <span className="font-medium">{latestDateText}</span>
        </span>
        <span className="text-label-small font-medium text-neutral-400 whitespace-nowrap">
          {LATEST_CONFIDENCE_TEXT}
        </span>
      </div>
    </>
  );
};

export interface ShippingSLAFootnoteProps {
  className?: string;
}

/** Renders the "limited data" footnote — auto-hidden for measured tier. */
const ShippingSLAFootnote = ({ className }: ShippingSLAFootnoteProps) => {
  const { tier } = useShippingSLAContext();
  if (tier !== SHIPPING_SLA_TIER.Fallback) {
    return null;
  }
  return (
    <span className={cn('text-label-small font-medium text-neutral-400', className)}>{LIMITED_DATA_FOOTNOTE}</span>
  );
};

export interface ShippingSLAPillProps {
  /** Wrapper classes (e.g. swap pill background) */
  className?: string;
  /** Icon classes (e.g. swap icon color) */
  iconClassName?: string;
}

/** Renders the seasonal-notice pill — auto-hidden when no `seasonalNoticeText`. */
const ShippingSLAPill = ({ className, iconClassName }: ShippingSLAPillProps) => {
  const { seasonalNoticeText } = useShippingSLAContext();
  if (!seasonalNoticeText) {
    return null;
  }
  return (
    <div
      className={cn(
        'flex gap-2 items-start tablet:items-center bg-primary-50 rounded-lg px-3 py-2 tablet:py-1 w-fit',
        className,
      )}
    >
      <LucideIcon name="ClockFading" size={16} className={cn('text-primary-500 shrink-0', iconClassName)} />
      <span className="text-label-medium font-regular text-neutral-700 tablet:whitespace-nowrap">
        {seasonalNoticeText}
      </span>
    </div>
  );
};

export const ShippingSLA = Object.assign(ShippingSLARoot, {
  Header: ShippingSLAHeader,
  Dates: ShippingSLADates,
  Footnote: ShippingSLAFootnote,
  Pill: ShippingSLAPill,
});

/**
 * Loading skeleton mirroring the SLA block footprint — two rows + header.
 * Use as QueryBoundary / Suspense `loadingFallback` for the SLA block.
 */
export const ShippingSLASkeleton = ({ className }: { className?: string }) => (
  <div className={cn('flex flex-col gap-3 items-start', className)} aria-busy="true">
    <Skeleton className="h-7 w-56" />
    <div className="flex flex-col gap-2 items-start">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-7 w-64" />
    </div>
  </div>
);
